// Anna Tool Definitions
// =====================
// Defines the 4 function calling tools Anna uses to interact with the learning workspace.
// These tools let Anna render structured content, update the concept map,
// present exercises, and track Bloom's taxonomy progression.
//
// Tool schema follows Anthropic's format — kept in sync with the API route
// that forwards them to Claude Haiku.

import type { Tool } from '@anthropic-ai/sdk/resources/messages';

export type ToolName =
  | 'show_content_block'
  | 'show_concept_map'
  | 'show_exercise'
  | 'update_bloom_level';

export const ANNA_TOOLS: Tool[] = [
  {
    name: 'show_content_block',
    description:
      'Display a rich content block to the student — use for definitions, examples, analogies, or summaries. Prefer this over inline prose when introducing a concept formally.',
    input_schema: {
      type: 'object',
      properties: {
        title: {
          type: 'string',
          description: 'Short heading for the content block',
        },
        content: {
          type: 'string',
          description: 'Body text in markdown format',
        },
        type: {
          type: 'string',
          enum: ['definition', 'example', 'analogy', 'summary'],
          description: 'Visual style and semantic purpose of the block',
        },
        nodeRef: {
          type: 'string',
          description: 'ID of the concept node this content block relates to',
        },
        depth: {
          type: 'string',
          enum: ['full', 'condensed', 'minimal'],
          description: 'Detail level: full (first exposure), condensed (returning), minimal (well-known)',
        },
      },
      required: ['title', 'content', 'type'],
    },
  },

  {
    name: 'show_concept_map',
    description:
      'Add nodes/edges to the concept map, highlight a node to draw attention, or update a node\'s Bloom level. Call after introducing a new concept or detecting a cognitive shift.',
    input_schema: {
      type: 'object',
      properties: {
        action: {
          type: 'string',
          enum: ['add_nodes', 'activate_node', 'highlight', 'update_bloom'],
          description: 'Action: add_nodes (tilføj nye noder), activate_node (tænd dormant noder), highlight (fremhæv noder), update_bloom (opdater bloom-niveau)',
        },
        nodes: {
          type: 'array',
          description: 'Nodes to add or update (used with add_nodes and update_bloom)',
          items: {
            type: 'object',
            properties: {
              id: {
                type: 'string',
                description: 'Stable identifier for the concept (snake_case)',
              },
              label: {
                type: 'string',
                description: 'Human-readable concept name',
              },
              bloomLevel: {
                type: 'string',
                enum: ['recognize', 'understand', 'apply', 'analyze', 'evaluate', 'create'],
                description: 'Current Bloom level for this concept',
              },
            },
            required: ['id', 'label'],
          },
        },
        edges: {
          type: 'array',
          description: 'Directed relationships between concepts',
          items: {
            type: 'object',
            properties: {
              source: {
                type: 'string',
                description: 'Source node id',
              },
              target: {
                type: 'string',
                description: 'Target node id',
              },
              label: {
                type: 'string',
                description: 'Optional edge label describing the relationship',
              },
              relationType: {
                type: 'string',
                enum: ['related', 'interdependent', 'causal'],
                description: 'Semantic type: related (connected), interdependent (mutual dependency), causal (one causes the other). Defaults to related.',
              },
            },
            required: ['source', 'target'],
          },
        },
        highlight: {
          type: 'string',
          description: 'Node id to visually highlight (used with highlight action)',
        },
        nodeIds: {
          type: 'array',
          items: { type: 'string' },
          description: 'Node IDs to activate or highlight',
        },
      },
      required: ['action'],
    },
  },

  {
    name: 'show_exercise',
    description:
      'Present an interactive exercise to the student. Use to create productive friction (Dialog/Exam modes) — not in Introduction unless the student explicitly asks to practice.',
    input_schema: {
      type: 'object',
      properties: {
        type: {
          type: 'string',
          enum: [
            'fill_in_blank',
            'multiple_choice',
            'comparison_table',
            'explain_in_own_words',
          ],
          description: 'Exercise format',
        },
        question: {
          type: 'string',
          description: 'The exercise prompt or question',
        },
        options: {
          type: 'array',
          description: 'Answer choices (required for multiple_choice)',
          items: { type: 'string' },
        },
        correct_answer: {
          type: 'string',
          description: 'Expected answer — used for immediate feedback when available',
        },
        hint: {
          type: 'string',
          description: 'Optional hint shown on request',
        },
      },
      required: ['type', 'question'],
    },
  },

  {
    name: 'update_bloom_level',
    description:
      "Update the student's demonstrated Bloom level for a specific concept. Call when you observe evidence of a cognitive shift — e.g. student explains in own words (understand), applies to new case (apply), or critiques an argument (evaluate).",
    input_schema: {
      type: 'object',
      properties: {
        concept: {
          type: 'string',
          description: 'Concept name or node id',
        },
        level: {
          type: 'string',
          enum: ['recognize', 'understand', 'apply', 'analyze', 'evaluate', 'create'],
          description: 'New Bloom level to assign',
        },
        reason: {
          type: 'string',
          description: 'Brief justification — helps with transparency and debugging',
        },
      },
      required: ['concept', 'level'],
    },
  },
];

/**
 * Tool set for exam mode — excludes concept map modification.
 * Anna can still highlight nodes and update Bloom levels, but cannot
 * add nodes, edges, or modify the map structure.
 */
export const EXAM_TOOLS: Tool[] = ANNA_TOOLS.map(tool => {
  if (tool.name === 'show_concept_map') {
    return {
      ...tool,
      description: 'Highlight a node on the concept map to draw attention, or update a node\'s Bloom level. In exam mode, you cannot add or modify nodes/edges — if you notice the map is missing important concepts, tell the student to revisit Introduction or Conversation first.',
      input_schema: {
        type: 'object' as const,
        properties: {
          action: {
            type: 'string',
            enum: ['highlight', 'update_bloom'],
            description: 'What to do with the concept map (add_nodes is not available in exam mode)',
          },
          nodes: (tool.input_schema as { properties: Record<string, unknown> }).properties.nodes,
          highlight: (tool.input_schema as { properties: Record<string, unknown> }).properties.highlight,
        },
        required: ['action'],
      },
    };
  }
  return tool;
});

/**
 * Tool set for introduction mode — allows activate_node and highlight,
 * but not add_nodes (model is structurally fixed from didacticConfig).
 */
export const INTRO_TOOLS: Tool[] = ANNA_TOOLS.filter(
  (tool) => tool.name !== 'show_exercise'
).map((tool) => {
  if (tool.name === 'show_concept_map') {
    return {
      ...tool,
      input_schema: {
        ...tool.input_schema,
        properties: {
          ...(tool.input_schema as { properties: Record<string, unknown> }).properties,
          action: {
            type: 'string',
            enum: ['activate_node', 'highlight', 'update_bloom'],
            description: 'Introduction mode: activate_node (tænd noder), highlight, update_bloom',
          },
        },
        required: ['action'],
      },
    };
  }
  return tool;
});
